import { Dispatch } from '@reduxjs/toolkit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import {
  selectFrontendColorCorrection,
  selectFrontendColorCorrectionProcessDarkImagesOverride,
  selectImageResolution,
  setFrontendColorCollectionResult,
} from '../../imageSlice';

const WORKER_URL = '/workers/openCVWorker.js';
export const MAX_BACKEND_CLIP_LIMIT = 10;
const RETRY_ATTEMPTS_PER_MEMORY_THRESHOLD = 2; // Number of times to retry at each memory threshold.  For example, if set to 2, it will retry twice at 128MB, then twice at 256MB, etc.

/**
 * useFrontendColorCorrection Hook
 *
 * This hook takes in an HTMLImageElement and applies CLAHE-based contrast correction
 * logic using a Web Worker running OpenCV.js.  Running this logic in a Web Worker allows us to overcome the WASM memory limitations in the browser that
 * were causing the OpenCV memory to grow slowly overtime and crash.  By running this in a Web Worker, we can respawn the worker as a workaround to reset the memory
 * when certain thresholds are exceeded without having to reload the page.
 *
 * It:
 * - Spawns and manages a Web Worker that loads OpenCV.js for image processing.
 * - Uses a loading state to track when image processing is ongoing.
 * - Waits until the worker signals it's ready before processing images.
 * - Handles memory warnings by respawning the worker.
 * - Returns the corrected image or the original if no correction is needed.
 *
 * @param isEnabled Whether the hook is enabled.  Default is true which loads the worker in advance.  If false, the worker is not loaded preemptively and the original image is returned.  This is useful for the high quality download feature that we don't want to spawn a worker for unless the user requests it.
 * @param inputImage The original HTMLImageElement (or null).
 * @param memoryRespawnRetryThresholds An optional number of memory levels to respawn the worker at if memory is exceeded. It is an array of memory levels in 128MB increments with each item in the array signifying a retry attempt at that level. For example, [128, 256, 512] would attempt to respawn the worker at 128MB, then 256MB, and finally 512MB.
 * @returns An object with finalImage (corrected or fallback to original) and retriesExceeded (boolean).
 */
export function useFrontendColorCorrection(
  isEnabled = true,
  inputImage?: HTMLImageElement | null,
  memoryRespawnRetryThresholds = [128]
): {
  finalImage: HTMLImageElement | null | undefined;
  retriesExceeded: boolean;
} {
  const dispatch = useAppDispatch();
  const frontendColorCorrection = useAppSelector(selectFrontendColorCorrection);
  const processDarkImagesOverride = useAppSelector(
    selectFrontendColorCorrectionProcessDarkImagesOverride
  );
  // Holds the current image to be processed or displayed
  const [image, setImage] = useState<HTMLImageElement | null | undefined>(
    inputImage
  );
  // After processing, finalImage is the resulting corrected image
  const [finalImage, setFinalImage] = useState<
    HTMLImageElement | null | undefined
  >(null);
  const imageResolution = useAppSelector(selectImageResolution);
  // Memory management indices to decide which threshold to try next
  const [currMemoryRespawnIndex, setCurrMemoryRespawnIndex] = useState(0);
  const [currMemoryRespawnThreshold, setCurrMemoryRespawnThreshold] = useState(
    memoryRespawnRetryThresholds[currMemoryRespawnIndex]
  );
  // Remember the last working threshold index to skip lower thresholds when processing higher resolution images
  const lastWorkingMemoryThresholdIndex = useRef(0);
  // Tracks how many times we’ve retried the current memory threshold
  const retryPerMemoryThresholdCount = useRef(0);
  // A unique ID for each new image to re-initialize some states
  const [imageID, setImageID] = useState<string>(uuidv4());

  // Refs for web worker and state tracking
  const workerRef = useRef<Worker | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const isWorkerReadyRef = useRef<boolean>(false);

  // If a new image arrives, generate a fresh UUID
  useEffect(() => {
    if (inputImage) {
      setImageID(uuidv4());
    }
  }, [inputImage]);

  // Reset memory attempts for each new image
  useEffect(() => {
    retryPerMemoryThresholdCount.current = 1;

    // If a last working memory threshold index is set, start from there since the user may have selected a higher resolution image that we know worked previously so we can start from there instead of the first memory retry threshold.
    if (lastWorkingMemoryThresholdIndex.current > 0) {
      const lastWorkingMemoryThreshold =
        memoryRespawnRetryThresholds[lastWorkingMemoryThresholdIndex.current];
      setCurrMemoryRespawnIndex(lastWorkingMemoryThresholdIndex.current);
      setCurrMemoryRespawnThreshold(lastWorkingMemoryThreshold);
    } else {
      setCurrMemoryRespawnIndex(0);
      setCurrMemoryRespawnThreshold(memoryRespawnRetryThresholds[0]);
    }
  }, [imageID]);

  // If the resolution changes (e.g., user picks a bigger image),
  // we see if we should start from a bigger memory threshold to avoid wasting time iterating through lower thresholds that will crash
  useEffect(() => {
    // If resolution is set to 4k (2160) or 8k (4320), try and find an index with a higher memory threshold to start from, else start from the beginning (128MB)
    let newIndex = 0;
    if (imageResolution >= 4320) {
      const foundIndex = memoryRespawnRetryThresholds.findIndex(
        (memoryRespawnRetryThresholds) => memoryRespawnRetryThresholds >= 512
      );
      if (foundIndex > 0) {
        newIndex = foundIndex;
      }
    } else if (imageResolution >= 2160) {
      const foundIndex = memoryRespawnRetryThresholds.findIndex(
        (memoryRespawnRetryThresholds) => memoryRespawnRetryThresholds >= 256
      );
      if (foundIndex > 0) {
        newIndex = foundIndex;
      }
    }

    lastWorkingMemoryThresholdIndex.current = newIndex;
  }, [imageResolution]);

  /**
   * Creates or recreates the OpenCV web worker to process images.  If a previous worker exists, it is terminated.
   * Also assigns event handlers for:
   *   - ready: Worker signals OpenCV is loaded
   *   - result: Processing done => we receive a data URL for the new image
   *   - memoryWarning: Worker warns memory is too high => we may respawn
   *   - error: Something failed => revert to original image if possible
   * @param memoryRespawnThreshold The maximum memory limit before respawning the worker (optional). Default is 128MB.
   */
  const spawnWorker = useCallback(
    (memoryRespawnThreshold = 128) => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null; // Ensure we drop references
      }

      const newWorker = new Worker(WORKER_URL);
      workerRef.current = newWorker;
      isWorkerReadyRef.current = false;

      /**
       * Handles the respawn logic for the worker based on the current memory threshold.
       * If the retry attempts for the current memory threshold have not been exceeded,
       * it retries at the same memory threshold. Otherwise, it respawns the worker at the next memory threshold level.
       * If all memory thresholds are exceeded, it logs an error, stops loading, and reverts to the original image if available.
       *
       * @param {number} currentThreshold - The current memory threshold for the worker.
       */
      function handleRespawn(currentThreshold: number) {
        // If we have not exceeded the retry attempts per memory threshold, retry at the same memory threshold, otherwise, respawn at the next level
        if (
          retryPerMemoryThresholdCount.current <
          RETRY_ATTEMPTS_PER_MEMORY_THRESHOLD
        ) {
          retryPerMemoryThresholdCount.current++;
          spawnWorker(currentThreshold);
        } else {
          // If memory limit exceeded, respawn worker at next level
          if (
            currMemoryRespawnIndex <
            memoryRespawnRetryThresholds.length - 1
          ) {
            const nextIndex = currMemoryRespawnIndex + 1;
            const newMemoryRespawnThreshold =
              memoryRespawnRetryThresholds[nextIndex];
            setCurrMemoryRespawnIndex(nextIndex);
            setCurrMemoryRespawnThreshold(newMemoryRespawnThreshold);
            spawnWorker(newMemoryRespawnThreshold);
          } else {
            console.error(
              'Color correction memory exceeded at all levels. Unable to process image on this device, disabling color correction.'
            );
            // Stop loading since an error occurred
            dispatch(
              setFrontendColorCollectionResult({
                finalClipLimit: 0,
                error: true,
                isLoading: false,
              })
            );
            lastWorkingMemoryThresholdIndex.current = 0;
            // Revert to original image if available
            if (imageRef.current) {
              setFinalImage(imageRef.current);
            }
          }
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      workerRef.current.onmessage = (e: MessageEvent<any>) => {
        const { action, dataURL, finalClipLimit, error, message } = e.data;

        if (action === 'ready') {
          isWorkerReadyRef.current = true;
          // If we have an image and color correction is enabled, process now that we are ready
          if (imageRef.current && frontendColorCorrection) {
            processImage(
              imageRef.current,
              workerRef.current,
              dispatch,
              currMemoryRespawnThreshold,
              processDarkImagesOverride
            );
          }
        } else if (action === 'result') {
          // Processing complete, worker returns a dataURL for the processed image
          const img = new Image();
          img.src = dataURL;
          img.onload = () => {
            setFinalImage(img);
          };
          // Stop loading since processing is complete
          dispatch(
            setFrontendColorCollectionResult({
              finalClipLimit,
              error,
              isLoading: false,
            })
          );
          // This threshold worked, so remember it in case of bigger images
          lastWorkingMemoryThresholdIndex.current = currMemoryRespawnIndex;
          // Reset the retry count for the next image since this one worked
          retryPerMemoryThresholdCount.current = 1;
        } else if (action === 'memoryWarning') {
          console.warn(
            'Color correction memory exceeded in worker. Re-spawning worker...'
          );
          handleRespawn(memoryRespawnThreshold);
        } else if (action === 'error') {
          console.error('Color correction worker error:', message);
          handleRespawn(memoryRespawnThreshold);
        }
      };

      workerRef.current.onerror = (err) => {
        console.error('Color correction worker encountered an error:', err);
        if (workerRef.current) {
          workerRef.current.terminate();
          workerRef.current = null;
        }

        handleRespawn(memoryRespawnThreshold);
      };

      return workerRef.current;
    },
    [
      dispatch,
      frontendColorCorrection,
      currMemoryRespawnIndex,
      currMemoryRespawnThreshold,
      processDarkImagesOverride,
    ]
  );

  // On mount, spawn the worker. On unmount, terminate it.
  useEffect(() => {
    if (isEnabled) {
      spawnWorker();
    }
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, [spawnWorker, isEnabled]);

  // Debounce changes to the input image to avoid rapid processing
  useDebounce(
    () => {
      setImage(inputImage);
    },
    300,
    [inputImage]
  );

  // Keep track of the current image for potential reprocessing
  useEffect(() => {
    if (image) {
      imageRef.current = image;
    } else {
      imageRef.current = null;
    }
  }, [image]);

  // If the user toggles color correction or the image changes,
  // we attempt to run the correction if the worker is ready
  useEffect(() => {
    if (!image) {
      setFinalImage(null);
      return;
    }

    if (!frontendColorCorrection) {
      console.log('Color correction disabled, returning original image.');
      setFinalImage(image);
      // Not loading since we do no processing
      dispatch(
        setFrontendColorCollectionResult({
          finalClipLimit: 0,
          error: false,
          isLoading: false,
        })
      );
      return;
    }

    if (currMemoryRespawnIndex >= memoryRespawnRetryThresholds.length) {
      console.error(
        'Color correction memory exceeded at all levels. Unable to process image on this device, disabling color correction.'
      );
      // Stop loading since an error occurred
      dispatch(
        setFrontendColorCollectionResult({
          finalClipLimit: 0,
          error: true,
          isLoading: false,
        })
      );
      // Revert to original image if available
      if (image) {
        setFinalImage(image);
      }
      return;
    }

    if (!workerRef.current) {
      console.error('Color correction worker Not Initialized.');
      return;
    }

    if (!isWorkerReadyRef.current) {
      console.log(
        'Color correction worker is not ready yet. Image will be processed once ready.'
      );
      // We do not set loading here since processing hasn't started
      return;
    }

    // Everything is ready, process now
    processImage(
      image,
      workerRef.current,
      dispatch,
      currMemoryRespawnThreshold,
      processDarkImagesOverride
    );
  }, [dispatch, image, frontendColorCorrection, processDarkImagesOverride]);

  return {
    finalImage,
    retriesExceeded:
      currMemoryRespawnIndex >= memoryRespawnRetryThresholds.length,
  };
}

/**
 * processImage
 * Sends the image to the worker, sets loading to true, and triggers the color correction process.
 * @param image The image to process
 * @param worker The worker instance
 * @param dispatch Redux dispatch
 * @param memoryRespawnThreshold The memory limit in MB before forcing a worker respawn
 * @param processDarkImagesOverride Whether to override the default behavior of not processing super dark images
 */
function processImage(
  image: HTMLImageElement,
  worker: Worker | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>,
  memoryRespawnThreshold?: number,
  processDarkImagesOverride?: boolean
) {
  if (!worker) return;

  // Create a temporary canvas to extract ImageData from the image
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error(
      'Color correction failed to get canvas context for image processing.'
    );
    return;
  }
  ctx.drawImage(image, 0, 0);
  const imageData = ctx.getImageData(0, 0, image.width, image.height);

  // Indicate loading start in redux
  dispatch(
    setFrontendColorCollectionResult({
      finalClipLimit: 0,
      error: false,
      isLoading: true,
    })
  );

  // Send the image and parameters to the worker for processing
  worker.postMessage({
    action: 'processImage',
    imageData: imageData,
    params: {
      memoryRespawnThreshold: memoryRespawnThreshold || null,
      processDarkImagesOverride,
    },
  });
}
